import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateContact } from "@/queries/hooks";
import { isValidPhoneNumber, normalizePhoneForStorage } from "@/lib/phone";
import { ContactType } from "@satyrsmc/shared/client";
import type { Contact } from "@satyrsmc/shared/client";
import { toDateOnly } from "@satyrsmc/shared/lib/date-utils";

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  /** When true, default the Hellenic checkbox to checked (e.g. when adding from Hellenics panel) */
  defaultHellenic?: boolean;
  /** Pre-fill first name (e.g. from a registration) */
  defaultFirstName?: string;
  /** Pre-fill last name (e.g. from a registration) */
  defaultLastName?: string;
  /** Pre-fill primary email (e.g. from a registration) */
  defaultEmail?: string;
  /** Pre-fill primary phone (e.g. from a registration) */
  defaultPhone?: string;
  /** Pre-fill mailing address (e.g. from a registration) */
  defaultAddressLine1?: string;
  defaultAddressLine2?: string;
  defaultCity?: string;
  defaultState?: string;
  defaultPostalCode?: string;
  /** Pre-fill emergency contact (e.g. from a registration) */
  defaultIceName?: string;
  defaultIcePhone?: string;
  /** Pre-fill birthday as ISO date string (e.g. from a registration) */
  defaultBirthday?: string;
}

export function AddContactDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultHellenic,
  defaultFirstName,
  defaultLastName,
  defaultEmail,
  defaultPhone,
  defaultAddressLine1,
  defaultAddressLine2,
  defaultCity,
  defaultState,
  defaultPostalCode,
  defaultIceName,
  defaultIcePhone,
  defaultBirthday,
}: AddContactDialogProps) {
  const createContactMutation = useCreateContact();
  const [type, setType] = useState<Contact["type"]>(ContactType.Person);
  const [displayName, setDisplayName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [clubName, setClubName] = useState("");
  const [role, setRole] = useState("");
  const [iceName, setIceName] = useState("");
  const [icePhone, setIcePhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [hellenic, setHellenic] = useState(defaultHellenic ?? false);
  const [deceased, setDeceased] = useState(false);
  const [deceasedYear, setDeceasedYear] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [icePhoneError, setIcePhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setHellenic(defaultHellenic ?? false);
      if (defaultFirstName) setFirstName(defaultFirstName);
      if (defaultLastName) setLastName(defaultLastName);
      if (defaultEmail) setPrimaryEmail(defaultEmail);
      if (defaultPhone) setPrimaryPhone(defaultPhone);
      if (defaultAddressLine1) setAddressLine1(defaultAddressLine1);
      if (defaultAddressLine2) setAddressLine2(defaultAddressLine2);
      if (defaultCity) setCity(defaultCity);
      if (defaultState) setState(defaultState);
      if (defaultPostalCode) setPostalCode(defaultPostalCode);
      if (defaultIceName) setIceName(defaultIceName);
      if (defaultIcePhone) setIcePhone(defaultIcePhone);
      if (defaultBirthday) setBirthday(toDateOnly(defaultBirthday));
      // Auto-build display name from pre-filled first/last
      if (defaultFirstName || defaultLastName) {
        setDisplayName([defaultFirstName, defaultLastName].filter(Boolean).join(" "));
      }
    }
  }, [
    open,
    defaultHellenic,
    defaultFirstName,
    defaultLastName,
    defaultEmail,
    defaultPhone,
    defaultAddressLine1,
    defaultAddressLine2,
    defaultCity,
    defaultState,
    defaultPostalCode,
    defaultIceName,
    defaultIcePhone,
    defaultBirthday,
  ]);

  const reset = () => {
    setType(ContactType.Person);
    setDisplayName("");
    setFirstName("");
    setLastName("");
    setOrgName("");
    setPrimaryEmail("");
    setPrimaryPhone("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setState("");
    setPostalCode("");
    setClubName("");
    setRole("");
    setIceName("");
    setIcePhone("");
    setBirthday("");
    setHellenic(defaultHellenic ?? false);
    setDeceased(false);
    setDeceasedYear("");
    setPhoneError(null);
    setIcePhoneError(null);
  };

  const handleSubmit = async () => {
    const name =
      displayName.trim() ||
      (type === "person" ? [firstName, lastName].filter(Boolean).join(" ") : orgName) ||
      "Unknown";
    if (!name) return;
    if (primaryPhone.trim() && !isValidPhoneNumber(primaryPhone)) {
      setPhoneError("Please enter a valid phone number (at least 10 digits)");
      return;
    }
    if (icePhone.trim() && !isValidPhoneNumber(icePhone)) {
      setIcePhoneError("Please enter a valid phone number (at least 10 digits)");
      return;
    }
    setPhoneError(null);
    setIcePhoneError(null);
    setSaving(true);
    try {
      await createContactMutation.mutateAsync({
        type,
        display_name: name,
        first_name: type === "person" ? firstName.trim() || null : null,
        last_name: type === "person" ? lastName.trim() || null : null,
        organization_name:
          type === "organization" ? orgName.trim() || null : orgName.trim() || null,
        club_name: clubName.trim() || null,
        role: role.trim() || null,
        hellenic,
        deceased,
        deceased_year: deceased && deceasedYear.trim() ? parseInt(deceasedYear, 10) : null,
        emails: primaryEmail.trim()
          ? [
              {
                id: "",
                contact_id: "",
                email: primaryEmail.trim(),
                type: "other" as const,
                is_primary: true,
              },
            ]
          : [],
        phones: normalizePhoneForStorage(primaryPhone)
          ? [
              {
                id: "",
                contact_id: "",
                phone: normalizePhoneForStorage(primaryPhone),
                type: "other" as const,
                is_primary: true,
              },
            ]
          : [],
        addresses:
          addressLine1.trim() || city.trim() || postalCode.trim()
            ? [
                {
                  id: "",
                  contact_id: "",
                  address_line1: addressLine1.trim() || null,
                  address_line2: addressLine2.trim() || null,
                  city: city.trim() || null,
                  state: state.trim() || null,
                  postal_code: postalCode.trim() || null,
                  country: "US",
                  type: "home" as const,
                  is_primary_mailing: true,
                },
              ]
            : [],
        emergency_contacts:
          iceName.trim() && normalizePhoneForStorage(icePhone)
            ? [
                {
                  id: "",
                  contact_id: "",
                  name: iceName.trim(),
                  phone: normalizePhoneForStorage(icePhone),
                  email: null,
                  relationship: null,
                },
              ]
            : [],
        birthday: birthday || null,
      });
      reset();
      onOpenChange(false);
      onSuccess();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as Contact["type"])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "person" ? (
            <>
              <div>
                <Label>Display name *</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Full name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First name</Label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div>
                  <Label>Last name</Label>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <div>
              <Label>Organization name *</Label>
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Company or org name"
              />
            </div>
          )}

          {type === "person" && (
            <div>
              <Label>Organization (optional)</Label>
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Affiliation"
              />
            </div>
          )}

          <div>
            <Label>Primary email</Label>
            <Input
              type="email"
              value={primaryEmail}
              onChange={(e) => setPrimaryEmail(e.target.value)}
            />
          </div>
          <div>
            <Label>Primary phone</Label>
            <Input
              value={primaryPhone}
              onChange={(e) => {
                setPrimaryPhone(e.target.value);
                setPhoneError(null);
              }}
              placeholder="(555) 123-4567"
            />
            {phoneError && <p className="text-sm text-destructive mt-1">{phoneError}</p>}
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Street address"
            />
            <Input
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Apt, suite, etc."
            />
            <div className="grid grid-cols-3 gap-2">
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="ZIP"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Emergency contact</Label>
            <Input
              value={iceName}
              onChange={(e) => setIceName(e.target.value)}
              placeholder="Name"
            />
            <Input
              value={icePhone}
              onChange={(e) => {
                setIcePhone(e.target.value);
                setIcePhoneError(null);
              }}
              placeholder="(555) 123-4567"
            />
            {icePhoneError && <p className="text-sm text-destructive mt-1">{icePhoneError}</p>}
          </div>

          <div>
            <Label>Birthday</Label>
            <DatePicker value={birthday} onChange={setBirthday} />
          </div>

          <div>
            <Label>Club / affiliation</Label>
            <Input value={clubName} onChange={(e) => setClubName(e.target.value)} />
          </div>
          <div>
            <Label>Role / title</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="add-contact-hellenic"
              checked={hellenic}
              onChange={(e) => setHellenic(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="add-contact-hellenic">Hellenic</Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="add-contact-deceased"
              checked={deceased}
              onChange={(e) => setDeceased(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="add-contact-deceased">Deceased</Label>
            {deceased && (
              <Input
                type="number"
                placeholder="Year (e.g. 2023)"
                value={deceasedYear}
                onChange={(e) => setDeceasedYear(e.target.value)}
                className="w-24"
                min={1900}
                max={2100}
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Add Contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
